<template>
<div>

  <!-- Begin Title Bar -->
  <v-toolbar tile elevation="2">
    <v-toolbar-title>{{ $t('ERC20TokenPage.title') }}</v-toolbar-title>

    <v-spacer></v-spacer>

    <v-btn icon @click="$router.go(-1)">
      <v-icon class="color-green">mdi-arrow-left</v-icon>
    </v-btn>
    <v-btn icon v-if="initializationDone" @click="refresh">
      <v-icon class="color-green">mdi-refresh</v-icon>
    </v-btn>
    <v-btn icon v-if="initializationDone" @click="showHelp = true">
      <v-icon class="color-green">mdi-help-circle</v-icon>
    </v-btn>

  </v-toolbar>
  <!-- End Title Bar -->

  <v-container>

    <Preloader v-if="!initializationDone" :text="$t('common.phrase.loading')" />

    <div v-if="initializationDone && tokenNotFound" class="my-4 pa-5 text-center">
      <div class="mb-3">Token Not Found</div>
        <BackButton></BackButton>
    </div>

    <v-expansion-panels v-if="initializationDone && !tokenNotFound"
      multiple v-model="tokenPanels" class="mb-6"
    >

      <!-- Begin token details -->
      <v-expansion-panel key="token-details">

        <v-expansion-panel-header>
          <span class="title">
            <v-icon color="grey">mdi-coin-outline</v-icon>
            {{ token.symbol }} ({{ token.name }}):
          </span>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <div v-bind:class="{'green lighten-5': !$vuetify.theme.dark, 'grey darken-2': $vuetify.theme.dark }" class="mb-2 pa-3">
            <div><strong>{{ $t('common.token.name') }}</strong>: {{ token.name }}</div>
            <div><strong>{{ $t('common.token.symbol') }}</strong>: {{ token.symbol }}</div>
            <div><strong>{{ $t('common.token.decimals') }}</strong>: {{ token.decimals }}</div>
            <div><strong>{{ $t('common.token.address') }}</strong>: <br />
              <a :href="networkConfig.tokenURL + token.address" target="_blank">
                <span class="d-inline-block text-truncate" style="max-width: 100%">{{ token.address }}</span>
              </a>
            </div>
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <!-- End token details -->

      <!-- Begin token Manage -->
      <v-expansion-panel key="token-manage">
        <v-expansion-panel-header key="manage-token">
          <span class="title"><v-icon color="grey">mdi-settings-outline</v-icon> {{ $t('ERC20TokenPage.titleManage') }}:</span>
        </v-expansion-panel-header>
        <v-expansion-panel-content>

          <v-row class="mt-n2">

            <v-col v-bind:class="{'green lighten-5': !$vuetify.theme.dark, 'grey darken-2': $vuetify.theme.dark }"  class="ma-2 text-center">

              <div class="mb-1 title">{{ $t('common.noun.balance') }}:</div>
              <div class="mb-1"><AmountNumber :value="account.balanceByAddress[token.address]" /></div>
              <div class="mb-1">
              	<v-btn color="secondary" small
	                :disabled="account.balanceByAddress[token.address] <= 0"
	                @click="showSendTokenForm()"
	              >
	                {{ $t('myTokensPage.actionTxtSend') }} {{ token.symbol }}
	              </v-btn>
	            </div>

            </v-col>

            <v-col v-bind:class="{'green lighten-5': !$vuetify.theme.dark, 'grey darken-2': $vuetify.theme.dark }"  class="ma-2 text-center">

              <div class="mb-0 title">{{ $t('common.noun.allowance') }}:</div>
              <div class="mt-n1 mb-0">
                <v-icon v-if="account.allowanceByAddress[token.address] > 0"
                  large color="success"
                  @click="showSetProxyAllowanceForm()"
                >mdi-toggle-switch</v-icon>
                <v-icon v-else
                  large color="grey lighten-1"
                  @click="showSetProxyAllowanceForm()"
                >mdi-toggle-switch-off-outline</v-icon>
              </div>
              <div class="mb-1">
                <v-btn color="secondary" small
                  @click="showSetProxyAllowanceForm()"
                >
                  <span v-if="account.allowanceByAddress[token.address] > 0">
                    {{ $t('myTokensPage.actionTxtRemoveAllowance') }}
                  </span>
                  <span v-else>
                    {{ $t('myTokensPage.actionTxtSetAllowance') }}
                  </span>
                </v-btn>
              </div>
            </v-col>

            <v-col v-bind:class="{'green lighten-5': !$vuetify.theme.dark, 'grey darken-2': $vuetify.theme.dark }"  class="ma-2 text-center">

              <div class="mb-0 title">{{ $t('ERC20TokenPage.titleTokenTracking') }}:</div>
              <div class="mt-n1 mb-0">
              	<v-icon v-if="isTokenTracked" large color="success"
	                @click="showRemoveTokenTrackingForm()"
	                >mdi-toggle-switch</v-icon>
	              <v-icon v-else large color="grey lighten-1"
	                @click="showAddTokenTrackingForm()"
	                >mdi-toggle-switch-off-outline</v-icon>
	            </div>
              <div class="mb-1">
                <v-btn v-if="isTokenTracked" color="secondary" small
                  @click="showRemoveTokenTrackingForm()"
                >
                  {{ $t('ERC20TokenPage.btnRemoveTokenTracking') }}
                </v-btn>
                <v-btn v-else color="secondary"
                  @click="showAddTokenTrackingForm()"
                >
                  {{ $t('ERC20TokenPage.btnAddTokenTracking') }}
                </v-btn>
              </div>
            </v-col>

          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <!-- End token Manage -->

      <!-- Begin WETH Wrapping and Unwrapping -->
      <v-expansion-panel key="token-weth" v-if="token.address === tokenInfo.WETHToken.address">
        <v-expansion-panel-header>
          <span class="title"><v-icon color="grey">mdi-bag-personal-outline</v-icon> {{ $t('ERC20TokenPage.titleWrapUnwrap') }}:</span>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-row dense>

            <v-col>
              <!-- Begin create sell order -->
              <div class="text-center">
                <v-btn color="primary"
                  @click="showWrapETHForm()"
                >{{ $t('compWrap.title') }}</v-btn>
              </div>
              <!-- End create sell order -->
            </v-col>

            <v-col>
              <!-- Begin create buy order -->
              <div class="text-center">
                <v-btn color="primary"
                  @click="showUnwrapETHForm()"
                >{{ $t('compUnwrap.title') }}</v-btn>
              </div>
              <!-- End create buy order -->
            </v-col>

          </v-row>

        </v-expansion-panel-content>
      </v-expansion-panel>
      <!-- End WETH Wrapping and Unwrapping -->

      <!-- Begin Marketplace -->
      <v-expansion-panel key="token-marketplace">
        <v-expansion-panel-header>
          <span class="title"><v-icon color="grey">mdi-finance</v-icon> {{ $t('ERC20TokenPage.titleMarketplace') }}:</span>
        </v-expansion-panel-header>
        <v-expansion-panel-content>

          <div class="subtitle-1 mb-2"><strong>{{ $t('ERC20TokenPage.titleTrade') }}</strong>:</div>

          <v-row dense class="mb-4">

            <v-col>
              <!-- Begin create sell order -->
              <div class="text-center">
                <v-btn color="primary"
                  :to="{ name: 'NewOrderByMaker', params: { makerAssetAddress: token.address } }"
                >{{ $t('myTokensPage.actionTxtSell') }} {{ token.symbol }}</v-btn>
              </div>
              <!-- End create sell order -->
            </v-col>

            <v-col>
              <!-- Begin create buy order -->
              <div class="text-center">
                <v-btn color="primary"
                  :to="{ name: 'NewOrderByTaker', params: { takerAssetAddress: token.address } }"
                >{{ $t('myTokensPage.actionTxtBuy') }} {{ token.symbol }}</v-btn>
              </div>
              <!-- End create buy order -->
            </v-col>

          </v-row>

          <div class="subtitle-1 mb-3"><strong>{{ $t('ERC20TokenPage.titleActiveTradingPairs') }}</strong>:</div>
          <div v-if="supportedTradingPairList.length > 0" class="mb-4">

            <v-chip v-for="(tradingPair, index) in supportedTradingPairList"
              :key="index" class="mr-2 mb-1" color="primary"
              :to="{ name: 'OrdersByTokenPair', params: {
                makerAssetSymbol: tradingPair.tokenA.symbol,
                takerAssetSymbol: tradingPair.tokenB.symbol,
                makerAssetAddress: tradingPair.tokenA.address,
                takerAssetAddress: tradingPair.tokenB.address
              } }"
            >
            {{ tradingPair.tokenA.symbol }} - {{ tradingPair.tokenB.symbol }}
            </v-chip>
          </div>
          <div v-else v-bind:class="{'grey lighten-4': !$vuetify.theme.dark, 'grey darken-2': $vuetify.theme.dark }" class="text-center pa-3 mb-4">
            {{ $t('ERC20TokenPage.displayNone') }}
          </div>

          <div class="subtitle-1 mb-3"><strong>{{ $t('ERC20TokenPage.titleTrackedTradingPairs') }}</strong>:</div>
          <div v-if="trackedTradingPairsWithToken.length > 0" class="mt-4">
            <v-chip v-for="(tradingPair, index) in trackedTradingPairsWithToken"
              :key="index" class="mr-2 mb-1" color="primary"
              :to="{ name: 'OrdersByTokenPair', params: {
                makerAssetSymbol: tradingPair.tokenA.symbol,
                takerAssetSymbol: tradingPair.tokenB.symbol,
                makerAssetAddress: tradingPair.tokenA.address,
                takerAssetAddress: tradingPair.tokenB.address
              } }"
            >
            {{ tradingPair.tokenA.symbol }} - {{ tradingPair.tokenB.symbol }}
            </v-chip>
          </div>
          <div v-else v-bind:class="{'grey lighten-4': !$vuetify.theme.dark, 'grey darken-2': $vuetify.theme.dark }" class="text-center pa-3 mb-4">
            {{ $t('ERC20TokenPage.displayNone') }}
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <!-- End Marketplace -->

      <!-- Begin Instant Buy -->
      <v-expansion-panel key="instant-buy" v-if="token.address !== tokenInfo.WETHToken.address">
        <v-expansion-panel-header>
          <span class="title"><v-icon color="grey">mdi-shopping-outline</v-icon> {{ $t('instantBuyTokenPage.title') }}:</span>
        </v-expansion-panel-header>
        <v-expansion-panel-content>

          <ZeroExInstant
            :token-address="token.address"
            v-on:zero-ex-instant-success="refresh()"
          ></ZeroExInstant>

        </v-expansion-panel-content>
      </v-expansion-panel>
      <!-- End Instant Buy -->

    </v-expansion-panels>


  </v-container><!-- v-container -->

  <!-- Begin Help -->
  <v-bottom-sheet v-model="showHelp">

    <v-card tile>

      <!--Modal Title -->
      <v-toolbar flat v-bind:class="{'green lighten-3': !$vuetify.theme.dark, 'grey darken-2': $vuetify.theme.dark }">
        <v-toolbar-title><v-icon>mdi-help-circle</v-icon> {{ $t('nav.help') }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="showHelp = false">
          <v-icon>mdi-window-close</v-icon>
        </v-btn>
      </v-toolbar>

      <!-- Modal Contents -->
      <v-card-text class="py-5 px-3" style="max-height: 400px; overflow-y: scroll;">
        <TokenHelp class="mb-2"></TokenHelp>
      </v-card-text>

    </v-card>

  </v-bottom-sheet>
  <!-- End Help -->

  <WrapETH v-if="initializationDone"
    ref="wrapETH" :fullscreen="isMobile"
  />

  <UnwrapETH v-if="initializationDone"
    ref="unwrapETH" :fullscreen="isMobile"
  />

  <SetProxyAllowance v-if="initializationDone && !tokenNotFound"
    ref="setProxyAllowanceFormMyTokens" :fullscreen="isMobile"
  />

  <AddTokenTracking v-if="initializationDone && !tokenNotFound"
    ref="addTokenTrackingForm" :fullscreen="isMobile"
  />

  <RemoveTokenTracking v-if="initializationDone && !tokenNotFound"
    v-on:token-tracking-removed="init()"
    ref="removeTokenTrackingForm" :fullscreen="isMobile"
  />

  <SendToken v-if="initializationDone && !tokenNotFound"
    ref="sendTokenForm" :fullscreen="isMobile"
  />

</div>
</template>

<script>
import { config } from '@/config'
import {mapActions, mapGetters, mapState} from 'vuex'
import ERC20Token from "@/components/ERC20Token"
import BackButton from "@/components/BackButton"
import AmountNumber from "@/components/AmountNumber"
import SetProxyAllowance from "@/components/SetProxyAllowance"
import WrapETH from "@/components/WrapETH"
import UnwrapETH from "@/components/UnwrapETH"
import AddTokenTracking from "@/components/AddTokenTracking"
import RemoveTokenTracking from "@/components/RemoveTokenTracking"
import SendToken from "@/components/SendToken"
import ZeroExInstant from "@/components/ZeroExInstant"
import { formatAmountNumber, formatAllowanceNumber } from '@/utils/utils'
import Preloader from "@/components/Preloader"
import { HttpClient } from '@0x/connect'
import TokenHelp from "@/components/help/TokenHelp"
import { assetDataUtils } from '@0x/order-utils'

export default {
  name: 'ERC20TokenPage',
  components: {
    ERC20Token,
    Preloader,
    BackButton,
    AmountNumber,
    SetProxyAllowance,
    AddTokenTracking,
    RemoveTokenTracking,
    SendToken,
    ZeroExInstant,
    WrapETH,
    UnwrapETH,
    TokenHelp,
  },
  data () {
    return {
      config: config,
      tokenAddress: this.$route.params.tokenAddress,
      token: null,
      tokenNotFound: false,
      supportedTradingPairList: null,
      initializationDone: false,
      showHelp: false,
      tokenPanels: [ 0, 1, 2, 3 ],
    }
  },
  computed: {
    ...mapGetters({
      network: 'getNetwork',
      account: 'getAccount',
      tokenInfo: 'getTokenInfo',
      relayerList: 'getRelayerList',
      relayerInfo: 'getRelayerInfo',
      myAddress: 'getUserAccount',
      web3Wrapper: 'getWeb3Wrapper',
      providerEngine: 'getProviderEngine',
      contractWrappers: 'getContractWrappers',
      contractAddresses: 'getContractAddresses',
      networkConfig: 'getNetworkConfig',
    }),
    isMobile: function () {
      return this.$vuetify.breakpoint.xsOnly
    },
    isTokenTracked: function () {
      if (this.account.trackedTokens) {
        for (let trackedToken of this.account.trackedTokens) {
          if (this.token.address.toLowerCase() === trackedToken.address.toLowerCase()) {
            return true
          }
        }
        return false
      }
      else {
        return false
      }
    },
    trackedTradingPairsWithToken: function () {
      if (this.token && !this.tokenNotFound) {
        const list = []
        for (let tokenPair of this.account.trackedTokenPairs) {
          if (tokenPair.tokenA.address.toLowerCase() === this.token.address.toLowerCase() ||
            tokenPair.tokenB.address.toLowerCase() === this.token.address.toLowerCase()) {
            list.push(tokenPair)
          }
        }
        return list
      }
      else {
        return []
      }
    },
  },
  methods: {
    ...mapActions(['loadNetwork', 'loadTokenInfo', 'loadAccount', 'loadRelayerInfo', 'getTokenInfoByAddress', 'getTokenInfoByAssetData', 'updateAccountForToken']),
    formatAmountNumber: formatAmountNumber,
    formatAllowanceNumber: formatAllowanceNumber,
    /* ------------------------------------------------------ */
    async init () {
      this.initializationDone = false
      this.token = null
      this.supportedTradingPairList = null
      this.tokenNotFound = false
      try {
        await this.loadNetwork()
        await this.loadTokenInfo()
        await this.loadAccount()
        await this.loadRelayerInfo()
        await this.loadToken()
        await this.loadSupportedTradingPairs()
      } catch (err) {
        console.log(err)
      }
      // console.log(this.tokenInfo)
      // console.log(this.relayerInfo)
      // console.log(this.account.trackedTokenPairs)
      // console.log(this.trackedTradingPairsWithToken)
      // console.log(this.supportedTradingPairList)
      this.initializationDone = true
    },

    async refresh () {
      this.init();
    },

    async loadToken () {
      // Load Token Info
      const token = await this.getTokenInfoByAddress(this.tokenAddress)
      if (token) {
        this.token = token
      }
      else {
        console.log('token Not Found')
        this.tokenNotFound = true
        return
      }

      const balance = this.account.balanceByAddress[this.tokenAddress]
      const allowance = this.account.allowanceByAddress[this.tokenAddress]
      if (balance === undefined || balance === null || allowance === undefined || allowance === null) {
        await this.updateAccountForToken(this.token)
        // await this.loadAccount(true)
        // console.log(this.account)
      }

    },

    async loadSupportedTradingPairs () {

      const tokenAssetData = assetDataUtils.encodeERC20AssetData(this.token.address)
      // console.log(tokenAssetData)
      const list = []
      const tokenInfo = {}
      for (let relayer in this.relayerInfo.ERC20AssetPairs){
        // console.log(relayer)
        const tradingPairList = Object.keys(this.relayerInfo.ERC20AssetPairs[relayer])
        // console.log(tradingPairList)
        for (let tradingPair of tradingPairList) {
          const [ assetA, assetB ] = tradingPair.split('-')
          // console.log(assetA)
          // console.log(assetB)
          if (assetA === tokenAssetData || assetB === tokenAssetData) {
            const tokenA = await this.getTokenInfoByAssetData(assetA)
            const tokenB = await this.getTokenInfoByAssetData(assetB)
            if (tokenA.symbol.match(/\S/) && tokenB.symbol.match(/\S/)) {
              const pair = {
                tokenA: {
                  symbol: tokenA.symbol,
                  address: tokenA.address,
                },
                tokenB: {
                  symbol: tokenB.symbol,
                  address: tokenB.address,
                }
              }
              list.push(pair)
            }
          }
        }
      }
      list.sort((a,b) => {
          return a.tokenA.symbol > b.tokenA.symbol ? 1 : a.tokenA.symbol < b.tokenA.symbol ? -1 : 0;
        })
      this.supportedTradingPairList = list
      // console.log(list)
    },

    showSetProxyAllowanceForm() {
      if (this.token) {
        const currentAllowance = this.account.allowanceByAddress[this.token.address]
        this.$refs.setProxyAllowanceFormMyTokens.showForm(this.token, currentAllowance)
      }
    },
    showWrapETHForm() {
      this.$refs.wrapETH.showForm()
    },
    showUnwrapETHForm() {
      this.$refs.unwrapETH.showForm()
    },
    showAddTokenTrackingForm() {
      if (this.token) {
        this.$refs.addTokenTrackingForm.showFormWithToken(this.token)
      }
    },
    showRemoveTokenTrackingForm() {
      if (this.token) {
        this.$refs.removeTokenTrackingForm.showForm(this.token)
      }
    },
    showSendTokenForm() {
      if (this.token) {
        this.$refs.sendTokenForm.showForm(this.token)
      }
    },

  },
  created () {
    this.init()
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.v-expansion-panel-header {
	padding: 4px 20px;
}
.v-expansion-panel-header .title {
	font-size: 1.1rem !important;
}
.v-expansion-panel--active > .v-expansion-panel-header {
	min-height: 50px;
}
</style>
